import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Gauge, Loader2, ShieldAlert, Unlock } from "lucide-react";
import { api } from "@/lib/api";
import { V } from "@/lib/vocab";
import { UsageByNumber } from "@/components/UsageByNumber";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

function AllowanceBar({ line }) {
  const pct = line.unlimited ? 0 : Math.min(100, line.pct || 0);
  const tone = line.unlimited ? "bg-emerald-500" : pct >= 100 ? "bg-redline-500" : pct >= 80 ? "bg-amber-500" : "bg-ink";
  return (
    <div data-testid={`usage-line-${line.key}`} className="rounded-xl border border-sand-200 bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-heading font-bold text-[15px] text-ink">{line.label}</div>
        <div className="text-sm text-ink/70 tabular-nums" data-testid={`usage-line-${line.key}-used`}>
          <strong className="text-ink">{fmt(line.used)}</strong>{" "}
          {line.unlimited ? `${line.unit} · unlimited` : `of ${fmt(line.allowance)} ${line.unit}`}
          {line.per_door != null && !line.unlimited && <span className="text-ink/45"> · {fmt(line.per_door)} per door</span>}
        </div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-sand-100 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-500 ${tone}`} style={{ width: `${line.unlimited ? 100 : pct}%` }} />
      </div>
      <div className="mt-1.5 flex flex-wrap justify-between gap-2 text-xs text-ink/55">
        <span>{line.unlimited ? "Never capped" : `${line.pct}% used`}</span>
        {line.overage_units > 0
          ? <span data-testid={`usage-line-${line.key}-overage`} className="font-semibold text-redline-600">{fmt(line.overage_units)} over × {money(line.overage_rate)} = {money(line.overage_usd)}</span>
          : !line.unlimited && <span>Overage {money(line.overage_rate)} per {line.unit.replace(/s$/, "")}</span>}
      </div>
    </div>
  );
}

export function UsageStatement({ tenantId, canEdit = true }) {
  const [st, setSt] = useState(null);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [ceiling, setCeiling] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (m = month) => {
    setLoading(true);
    return api.get(`/tenants/${tenantId}/usage/statement${m ? `?month=${m}` : ""}`)
      .then((r) => { setSt(r.data); setMonth(r.data.month); setCeiling(r.data.ceiling_usd ?? ""); })
      .catch(() => toast.error("Couldn't load the usage statement"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(""); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCeiling = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/usage/ceiling`, { overage_ceiling_usd: ceiling === "" ? null : Number(ceiling) });
      setSt(r.data); toast.success(ceiling === "" ? "Ceiling removed" : `Ceiling set at ${money(ceiling)}`);
    } catch { toast.error("Couldn't save the ceiling"); } finally { setBusy(false); }
  };
  const allow = async () => {
    setBusy(true);
    try { const r = await api.post(`/tenants/${tenantId}/usage/allow-overage`); setSt(r.data); toast.success("Overage allowed for this month"); }
    catch { toast.error("Couldn't allow overage"); } finally { setBusy(false); }
  };
  const download = async (ext) => {
    try {
      const r = await api.get(`/tenants/${tenantId}/usage/statement.${ext}?month=${st.month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `usage-${st.month}.${ext}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error(`Couldn't export the ${ext.toUpperCase()}`); }
  };

  if (loading && !st) return <div className="flex justify-center py-12 text-ink/40" data-testid="usage-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!st) return null;
  const months = [...new Set([st.month, ...Array.from({ length: 5 }, (_, i) => { const d = new Date(); d.setUTCMonth(d.getUTCMonth() - i); return d.toISOString().slice(0, 7); })])].sort().reverse();
  const inc = st.included || {};

  return (
    <div className="space-y-4" data-testid="usage-statement">
      <div className="rounded-2xl border border-sand-200 bg-white p-5 md:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="h-9 w-9 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><Gauge className="h-[18px] w-[18px] text-redline-500" strokeWidth={1.5} /></div>
            <div className="min-w-0">
              <h3 className="font-heading font-bold text-[15px] text-ink">Usage &amp; allowances</h3>
              <p className="text-sm text-muted-foreground mt-0.5">What&rsquo;s included every month for {st.doors} doors, what’s been used, and any overage. {V.one("person")} conversations and emergency alerts are never capped.</p>
            </div>
          </div>
          <select data-testid="usage-month" value={month} onChange={(e) => load(e.target.value)}
            className="w-full sm:w-auto rounded-full border border-sand-200 bg-white px-3 py-1.5 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-redline-500">
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {st.paused && (
          <div data-testid="usage-paused-banner" className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <span className="flex-1 min-w-[12rem]">Overage reached the {money(st.ceiling_usd)} ceiling. {V.one("org")}-wide texts and voice beyond the allowance are paused until a manager allows it.</span>
            {canEdit && <button data-testid="usage-allow-overage" onClick={allow} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-ink-700 disabled:opacity-50"><Unlock className="h-3.5 w-3.5" /> Allow overage this month</button>}
          </div>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">{st.lines.map((l) => <AllowanceBar key={l.key} line={l} />)}</div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-sand-50 px-4 py-3">
          <div className="text-sm text-ink/70">Overage this month</div>
          <div data-testid="usage-total-overage" className={`font-heading text-xl font-extrabold tabular-nums ${st.total_overage_usd > 0 ? "text-redline-600" : "text-ink"}`}>{money(st.total_overage_usd)}</div>
        </div>

        <div className="mt-4 text-xs text-ink/55" data-testid="usage-included">
          Included, never capped: <strong>{inc.resident_texts_in}</strong> {V.lc("person")} texts in · <strong>{inc.resident_replies}</strong> replies · <strong>{inc.calls}</strong> calls · <strong>{inc.emergency_texts}</strong> emergency texts · <strong>{inc.concierge_answers}</strong> AI answers
          {inc.unclassified_texts > 0 && <> · {inc.unclassified_texts} texts from before metering</>}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="usage-export-csv" onClick={() => download("csv")} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-sand-50"><Download className="h-3.5 w-3.5" /> CSV</button>
          <button data-testid="usage-export-pdf" onClick={() => download("pdf")} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-sand-50"><FileText className="h-3.5 w-3.5" /> PDF statement</button>
        </div>
      </div>

      <div className="rounded-2xl border border-sand-200 bg-white p-5 md:px-6" data-testid="usage-ceiling-card">
        <h3 className="font-heading font-bold text-[15px] text-ink">Monthly overage ceiling</h3>
        <p className="text-sm text-muted-foreground mt-0.5">Optional. When overage charges reach this amount, {V.lc("org")}-wide texts and voice minutes beyond the allowance pause until a manager allows more. Emergency alerts and {V.lc("person")} conversations keep going. Leave blank for no ceiling.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink/50">$</span>
            <input data-testid="usage-ceiling-input" type="number" min="0" step="5" value={ceiling} disabled={!canEdit}
              onChange={(e) => setCeiling(e.target.value)} placeholder="No ceiling"
              className="w-40 rounded-lg border border-sand-200 pl-7 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50" />
          </div>
          {canEdit && <button data-testid="usage-ceiling-save" onClick={saveCeiling} disabled={busy} className="rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-50">Save</button>}
          {st.overage_approved && <span className="text-xs text-emerald-700 font-semibold" data-testid="usage-overage-approved">Overage allowed for {st.month}</span>}
        </div>
      </div>

      <UsageByNumber tenantId={tenantId} month={st.month} />
    </div>
  );
}
